import {
  faBoltLightning,
  faChevronDown,
  faChevronUp,
  faMagnifyingGlass,
  faMapMarker,
  faSliders,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { useEffect, useRef, useState } from 'react';
import { Platform, Pressable, ScrollView, useWindowDimensions, View } from 'react-native';
import { useAppSelector } from '../../../hooks/store-hook';
import useTranslation from '../../../hooks/translation-hook';
import useCustomTheme from '../../../hooks/theme-hook';
import useStyles from '../../../hooks/styles-hook';
import { StylesHelper } from '../../../models/helpers/stylesHelper';
import Label, { LabelType } from '../Label';
import CustomButton from '../CustomButton';
import CustomCheckbox from '../CustomCheckbox';
import CustomSwitch from '../CustomSwitch';
import { FilterItemProps } from './FilterItem';
import { getFilterPanelTokens } from './filterPanelTokens';

export interface MapFilterProps {
  items: FilterItemProps[];
  selectedFilterType?: number;
  showMissionTypeFilters?: boolean;
  showSearchArea?: boolean;
  onlyAcceptableMissions?: boolean;
  showUpcomingMissions?: boolean;
  keepSwitchesOnOneRow?: boolean;
  switchCardsTransparent?: boolean;
  switchesBottomSpacing?: number;
  unavailableToggleLabel?: string;
  upcomingToggleLabel?: string;
  switchOnColor?: string;
  switchOffColor?: string;
  inlineMode?: boolean;
  tagPopupBackgroundColor?: string;
  tagPopupBorderColor?: string;
  tagPopupTitleColor?: string;
  tagPopupTextColor?: string;
  tagPopupCheckboxColor?: string;
  tagPopupCheckboxBackgroundColor?: string;
  filterBarBackgroundColor?: string;
  filterBarTextColor?: string;
  filterButtonBackgroundColor?: string;
  filterButtonIconColor?: string;
  filterIndicatorColor?: string;
  switchCardBackgroundColor?: string;
  switchCardTextColor?: string;
  switchCardBorderColor?: string;
  switchCardsEqualWidth?: number;
  showResetAction?: boolean;
  requireConfirmation?: boolean;
  topOffset?: number;
  loading?: boolean;
  open?: boolean;
  onChangeText?(text: string): void;
  onClear?(): void;
  onFocus?(e: any): void;
  onBlur?(e: any): void;
  onKeyPress?(e: any): void;
  onBack?(): void;
  onSelectionChange?(selectedItems: string[]): void;
  onSelectionChangeStart?(): void;
  onSearchAreaPress?(): void;
  onOpenChange?(state: boolean): void;
  onFilterTypeChange?(state: number): void;
  onOnlyAcceptableMissions?(e: any): void;
  onShowUpcomingMissions?(e: any): void;
  onConfirmSelection?(next: {
    selectedItems: string[];
    onlyAcceptableMissions: boolean;
    showUpcomingMissions: boolean;
    selectedFilterType: number;
  }): void;
}

const FILTER_TYPE_ALL = 0;
const FILTER_TYPE_CLASSIC = 1;
const FILTER_TYPE_FLASH = 2;
const FILTER_CHANGE_DEBOUNCE_MS = 500;

const MapFilter = (props: MapFilterProps) => {
  const t = useTranslation();
  const theme = useCustomTheme();
  const styles = useStyles((section) => section.customMap);
  const userPrefs = useAppSelector((state) => state.userPrefs);
  const layout = useWindowDimensions();

  const [showTagPopup, setShowTagPopup] = useState(false);
  const [showSearchArea, setShowSearchArea] = useState(false);
  const [filterItems, setFilterItems] = useState<FilterItemProps[]>([]);
  const [onlyAcceptableMissions, setOnlyAcceptableMissions] = useState(props.onlyAcceptableMissions);
  const [showUpcomingMissions, setShowUpcomingMissions] = useState(props.showUpcomingMissions);
  const [localSelectedFilterType, setLocalSelectedFilterType] = useState(props.selectedFilterType ?? FILTER_TYPE_ALL);
  const selectionDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectionDebounceRunIdRef = useRef(0);

  const isInlineMode = !!props.inlineMode;
  const requireConfirmation = !!props.requireConfirmation;
  const shouldRenderInlineBackdrop = showTagPopup && isInlineMode && !(Platform.OS === 'web' && requireConfirmation);
  const hasEqualSwitchCardWidth = typeof props.switchCardsEqualWidth === 'number' && props.switchCardsEqualWidth > 0;
  const selectedFilterType = props.selectedFilterType ?? FILTER_TYPE_ALL;
  const showMissionTypeFilters = props.showMissionTypeFilters ?? false;
  const showResetAction = props.showResetAction ?? true;
  const filterPanelWidth = isInlineMode ? '100%' : layout.width < 590 ? '100%' : 348;
  const filterPanelTokens = getFilterPanelTokens(theme, userPrefs.uiLanguage);
  const resetFiltersLabel = filterPanelTokens.resetFiltersLabel;
  const filterBarBackgroundColor = props.filterBarBackgroundColor ?? filterPanelTokens.triggerBackgroundColor;
  const filterBarTextColor = props.filterBarTextColor ?? filterPanelTokens.triggerTextColor;
  const filterButtonIconColor = props.filterButtonIconColor ?? filterPanelTokens.triggerTextColor;
  const tagPopupBackgroundColor = props.tagPopupBackgroundColor ?? filterPanelTokens.panelBackgroundColor;
  const tagPopupBorderColor = props.tagPopupBorderColor ?? filterPanelTokens.panelBorderColor;
  const businessTypeContainerBackgroundColor = props.tagPopupBackgroundColor ?? filterPanelTokens.businessTypeBackgroundColor;
  const tagPopupTitleColor = props.tagPopupTitleColor ?? filterPanelTokens.businessTypeTitleColor;
  const tagPopupTextColor = props.tagPopupTextColor ?? filterPanelTokens.textColor;
  const tagPopupCheckboxColor = props.tagPopupCheckboxColor ?? theme.text.primary.toString();
  const switchCardBackgroundColor = props.switchCardBackgroundColor ?? 'transparent';
  const switchCardBorderColor = props.switchCardBorderColor ?? filterPanelTokens.switchCardBorderColor;
  const switchCardTextColor = props.switchCardTextColor ?? filterPanelTokens.switchCardTextColor;
  const activeMissionTypePillTextColor = filterPanelTokens.activePillTextColor;

  useEffect(() => {
    setFilterItems(props.items ?? []);
  }, [props.items]);

  useEffect(() => {
    setShowTagPopup(!!props.open);
  }, [props.open]);

  useEffect(() => {
    setShowSearchArea(!!props.showSearchArea);
  }, [props.showSearchArea]);

  useEffect(() => {
    setOnlyAcceptableMissions(props.onlyAcceptableMissions);
  }, [props.onlyAcceptableMissions]);

  useEffect(() => {
    setShowUpcomingMissions(props.showUpcomingMissions);
  }, [props.showUpcomingMissions]);

  useEffect(() => {
    setLocalSelectedFilterType(props.selectedFilterType ?? FILTER_TYPE_ALL);
  }, [props.selectedFilterType]);

  useEffect(() => {
    props.onOpenChange?.(showTagPopup);
  }, [showTagPopup, props.onOpenChange]);

  useEffect(
    () => () => {
      if (selectionDebounceRef.current) {
        clearTimeout(selectionDebounceRef.current);
        selectionDebounceRef.current = null;
      }
    },
    [],
  );

  const emitSelectionChangeDebounced = (selectedIds: string[]) => {
    selectionDebounceRunIdRef.current += 1;
    const runId = selectionDebounceRunIdRef.current;

    if (selectionDebounceRef.current) {
      clearTimeout(selectionDebounceRef.current);
      selectionDebounceRef.current = null;
    }

    selectionDebounceRef.current = setTimeout(() => {
      if (runId !== selectionDebounceRunIdRef.current) {
        return;
      }
      props.onSelectionChange?.(selectedIds);
      selectionDebounceRef.current = null;
    }, FILTER_CHANGE_DEBOUNCE_MS);
  };

  const cancelPendingSelectionDebounce = () => {
    selectionDebounceRunIdRef.current += 1;
    if (selectionDebounceRef.current) {
      clearTimeout(selectionDebounceRef.current);
      selectionDebounceRef.current = null;
    }
  };

  const handleFilterChange = (checked: boolean, value: string) => {
    const next = filterItems.map((item) => (item.id === value ? { ...item, checked } : item));
    setFilterItems(next);
    if (!requireConfirmation) {
      props.onSelectionChangeStart?.();
      emitSelectionChangeDebounced(next.filter((x) => x.checked).map((x) => x.id));
    }
  };

  const handleSelectAll = () => {
    const shouldSelectAll = filterItems.some((x) => !x.checked);
    const next = filterItems.map((item) => ({ ...item, checked: shouldSelectAll }));
    setFilterItems(next);
    if (!requireConfirmation) {
      props.onSelectionChangeStart?.();
      emitSelectionChangeDebounced(next.filter((x) => x.checked).map((x) => x.id));
    }
  };

  const handleResetFilters = () => {
    const next = filterItems.map((item) => ({ ...item, checked: false }));
    setFilterItems(next);
    setLocalSelectedFilterType(FILTER_TYPE_ALL);
    setOnlyAcceptableMissions(false);
    setShowUpcomingMissions(true);

    if (!requireConfirmation) {
      cancelPendingSelectionDebounce();
      props.onSelectionChangeStart?.();
      props.onSelectionChange?.([]);

      if (showMissionTypeFilters && localSelectedFilterType !== FILTER_TYPE_ALL) {
        props.onFilterTypeChange?.(FILTER_TYPE_ALL);
      }

      if (onlyAcceptableMissions) {
        props.onOnlyAcceptableMissions?.(false);
      }

      if (!showUpcomingMissions) {
        props.onShowUpcomingMissions?.(true);
      }
    }
  };

  const handleApplyFilters = () => {
    cancelPendingSelectionDebounce();

    const selectedIds = filterItems.filter((x) => x.checked).map((x) => x.id);

    props.onSelectionChangeStart?.();
    if (props.onConfirmSelection) {
      props.onConfirmSelection({
        selectedItems: selectedIds,
        onlyAcceptableMissions: !!onlyAcceptableMissions,
        showUpcomingMissions: !!showUpcomingMissions,
        selectedFilterType: localSelectedFilterType,
      });
    } else {
      props.onSelectionChange?.(selectedIds);

      if (showMissionTypeFilters && localSelectedFilterType !== selectedFilterType) {
        props.onFilterTypeChange?.(localSelectedFilterType);
      }

      if (onlyAcceptableMissions !== props.onlyAcceptableMissions) {
        props.onOnlyAcceptableMissions?.(onlyAcceptableMissions);
      }

      if (showUpcomingMissions !== props.showUpcomingMissions) {
        props.onShowUpcomingMissions?.(showUpcomingMissions);
      }
    }

    setShowTagPopup(false);
  };

  const triggerContainerStyle = {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    borderRadius: 8,
    backgroundColor: filterBarBackgroundColor,
    borderWidth: 0,
    borderColor: tagPopupBorderColor,
    minHeight: 42,
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8,
    ...StylesHelper.shadow({ width: 1, height: 1 }, 0.14, 2, theme.overlayColor.toString(), 1),
  };

  const panelCardStyle = {
    marginTop: 6,
    borderRadius: 10,
    borderWidth: 0,
    borderColor: tagPopupBorderColor,
    backgroundColor: tagPopupBackgroundColor,
    paddingHorizontal: 10,
    paddingVertical: 10,
    ...StylesHelper.shadow({ width: 1, height: 2 }, 0.14, 3, theme.overlayColor.toString(), 1),
  };

  const missionTypePill = (type: number, icon?: any, label?: string) => {
    const active = localSelectedFilterType === type;
    return (
      <Pressable
        key={`filter-type-${type}`}
        onPress={() => {
          setLocalSelectedFilterType(type);
          if (!requireConfirmation) {
            props.onFilterTypeChange?.(type);
          }
        }}
        style={{
          flex: 1,
          borderRadius: 999,
          borderWidth: 1,
          borderColor: active ? filterBarTextColor : filterPanelTokens.inactivePillBorderColor,
          backgroundColor: active ? filterPanelTokens.activePillBackgroundColor : theme.background.primary,
          minHeight: 34,
          paddingHorizontal: 8,
          paddingVertical: 5,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 5,
        }}
      >
        {!!icon && (
          <FontAwesomeIcon
            icon={icon}
            size={11}
            color={active ? activeMissionTypePillTextColor : theme.text.primary.toString()}
          />
        )}
        <Label
          style={{
            color: active ? activeMissionTypePillTextColor : theme.text.primary,
            fontWeight: '600',
            fontSize: 13,
          }}
        >
          {label}
        </Label>
      </Pressable>
    );
  };

  return (
    <View
      style={[
        styles.searchFieldContainerMobileFix,
        props.topOffset !== undefined ? { top: props.topOffset } : undefined,
        isInlineMode
          ? {
              position: 'relative',
              top: 0,
              left: 0,
              width: '100%',
              maxWidth: '100%',
              paddingHorizontal: 0,
              overflow: 'visible',
              zIndex: showTagPopup ? 60 : 2,
              elevation: showTagPopup ? 60 : 2,
            }
          : undefined,
      ]}
      pointerEvents={isInlineMode ? 'auto' : 'box-none'}
    >
      {shouldRenderInlineBackdrop && (
        <Pressable
          onPress={() => setShowTagPopup(false)}
          style={{
            position: 'absolute',
            top: -2000,
            left: -2000,
            right: -2000,
            bottom: -2000,
            zIndex: 50,
            elevation: 50,
          }}
        />
      )}
      <View
        style={[
          {
            width: filterPanelWidth,
            maxWidth: '100%',
            alignSelf: 'stretch',
          },
          isInlineMode ? { width: '100%' } : undefined,
        ]}
      >
        <Pressable
          onPress={() => setShowTagPopup((prev) => !prev)}
          style={({ pressed }) => ({
            ...triggerContainerStyle,
            opacity: pressed ? 0.9 : 1,
          })}
        >
          <FontAwesomeIcon icon={faSliders} size={14} color={filterButtonIconColor} />
          <Label
            style={{
              color: filterBarTextColor,
              fontWeight: '700',
              fontSize: 13,
              textTransform: 'uppercase',
              flexGrow: 1,
              flexShrink: 1,
            }}
            variant={LabelType.titleDarkSmall}
            numberOfLines={1}
          >
            {t('Missions-filters')}
          </Label>
          <FontAwesomeIcon
            icon={showTagPopup ? faChevronUp : faChevronDown}
            size={12}
            color={filterButtonIconColor}
          />
        </Pressable>
      {showTagPopup && (
        <View
          style={[
            isInlineMode
              ? {
                  position: 'absolute',
                  top: 46,
                  left: 0,
                  right: 0,
                  zIndex: 60,
                  elevation: 60,
                }
              : undefined,
          ]}
        >
            <View style={panelCardStyle}>
              {showMissionTypeFilters && (
                <View style={{ flexDirection: 'row', gap: 8, marginBottom: 12 }}>
                  {missionTypePill(FILTER_TYPE_ALL, undefined, t('All'))}
                  {missionTypePill(FILTER_TYPE_CLASSIC, faMapMarker, t('Classic'))}
                  {missionTypePill(FILTER_TYPE_FLASH, faBoltLightning, t('Flash'))}
                </View>
              )}

              <View
                style={{
                  flexDirection: 'row',
                  flexWrap: props.keepSwitchesOnOneRow ? 'nowrap' : 'wrap',
                  gap: 8,
                  marginBottom: 10,
                }}
              >
                <View
                  style={{
                    display: 'flex',
                    gap: 7,
                    flexDirection: 'row',
                    backgroundColor: props.switchCardsTransparent ? 'transparent' : switchCardBackgroundColor,
                    borderColor: switchCardBorderColor,
                    borderWidth: 0,
                    alignItems: 'center',
                    paddingHorizontal: 2,
                    paddingVertical: 2,
                    flexShrink: 1,
                    flexGrow: props.keepSwitchesOnOneRow ? (hasEqualSwitchCardWidth ? 0 : 1) : 0,
                    width: props.keepSwitchesOnOneRow ? (hasEqualSwitchCardWidth ? props.switchCardsEqualWidth : '48%') : 'auto',
                    elevation: 0,
                    ...(props.switchCardsTransparent ? {} : StylesHelper.borderRadius(8)),
                  }}
                >
                  <CustomSwitch
                    isEnabled={!onlyAcceptableMissions}
                    onColor={props.switchOnColor}
                    offColor={props.switchOffColor}
                    onPress={(checked) => {
                      const nextOnlyAcceptableMissions = !checked;
                      setOnlyAcceptableMissions(nextOnlyAcceptableMissions);
                      if (!requireConfirmation) {
                        props.onOnlyAcceptableMissions?.(nextOnlyAcceptableMissions);
                      }
                    }}
                  />
                  <Label style={{ flexShrink: 1, color: switchCardTextColor, fontSize: 12, fontWeight: '600' }} numberOfLines={2}>
                    {props.unavailableToggleLabel ?? t('available_missions_only')}
                  </Label>
                </View>

                <View
                  style={{
                    display: 'flex',
                    gap: 7,
                    flexDirection: 'row',
                    backgroundColor: props.switchCardsTransparent ? 'transparent' : switchCardBackgroundColor,
                    borderColor: switchCardBorderColor,
                    borderWidth: 0,
                    alignItems: 'center',
                    paddingHorizontal: 2,
                    paddingVertical: 2,
                    flexShrink: 1,
                    flexGrow: props.keepSwitchesOnOneRow ? (hasEqualSwitchCardWidth ? 0 : 1) : 0,
                    width: props.keepSwitchesOnOneRow ? (hasEqualSwitchCardWidth ? props.switchCardsEqualWidth : '48%') : 'auto',
                    elevation: 0,
                    ...(props.switchCardsTransparent ? {} : StylesHelper.borderRadius(8)),
                  }}
                >
                  <CustomSwitch
                    isEnabled={showUpcomingMissions}
                    onColor={props.switchOnColor}
                    offColor={props.switchOffColor}
                    onPress={(checked) => {
                      setShowUpcomingMissions(checked);
                      if (!requireConfirmation) {
                        props.onShowUpcomingMissions?.(checked);
                      }
                    }}
                  />
                  <Label style={{ flexShrink: 1, color: switchCardTextColor, fontSize: 12, fontWeight: '600' }} numberOfLines={2}>
                    {props.upcomingToggleLabel ?? t('upcoming_missions')}
                  </Label>
                </View>
              </View>

              <View
                style={{
                  borderRadius: 10,
                  backgroundColor: businessTypeContainerBackgroundColor,
                  borderWidth: 0,
                  borderColor: tagPopupBorderColor,
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                }}
              >
                <Label
                  style={{
                    color: tagPopupTitleColor,
                    textTransform: 'uppercase',
                    fontSize: 12,
                    fontWeight: '700',
                    marginBottom: 8,
                  }}
                >
                  {t('filter_by_business_type')}
                </Label>
                <Pressable onPress={handleSelectAll} style={{ marginBottom: 8 }}>
                  <Label style={{ color: tagPopupTitleColor, fontSize: 12, fontWeight: '600' }}>
                    {filterItems.find((x) => !x.checked) ? t('select_all') : t('deselect_all')}
                  </Label>
                </Pressable>

                <ScrollView style={{ maxHeight: 168 }}>
                  {filterItems.map((item) => (
                    <CustomCheckbox
                      key={`tag_${item.id}`}
                      small
                      id={`tag_${item.id}`}
                      checked={item.checked}
                      value={item.id}
                      label={item.name}
                      boxStyle={props.tagPopupCheckboxBackgroundColor ? { backgroundColor: props.tagPopupCheckboxBackgroundColor } : undefined}
                      textStyle={tagPopupTextColor ? { color: tagPopupTextColor, fontSize: 12 } : { fontSize: 12 }}
                      style={{ paddingHorizontal: 0, paddingVertical: 4 }}
                      onChange={(checked, value) => {
                        handleFilterChange(checked, `${value}`);
                      }}
                    />
                  ))}
                </ScrollView>
              </View>

              {showResetAction && (
                <Pressable onPress={handleResetFilters} style={{ marginTop: 8, alignSelf: 'flex-start' }}>
                  <Label
                    style={{
                      color: tagPopupTitleColor,
                      fontWeight: '600',
                      fontSize: 12,
                    }}
                  >
                    {resetFiltersLabel}
                  </Label>
                </Pressable>
              )}

              {requireConfirmation && (
                <CustomButton
                  title={t('confirm')}
                  style={{ marginTop: 12 }}
                  onPress={handleApplyFilters}
                  disabled={!!props.loading}
                />
              )}
            </View>
          </View>
        )}
      </View>

      <View style={styles.searchThisAreaContainerBase}>
        {!isInlineMode && showSearchArea && !showTagPopup && (
          <Pressable
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              paddingHorizontal: 12,
              paddingVertical: 8,
              ...StylesHelper.shadow({ width: 2, height: 3 }, 0.2, 5, 'black', 3),
              backgroundColor: theme.background.lightSea,
              gap: 8,
              borderRadius: 50,
            }}
            onPress={() => props.onSearchAreaPress?.()}
          >
            <FontAwesomeIcon icon={faMagnifyingGlass} size={20} color={theme.text.darkTurquoiseTwo.toString()} />
            <Label style={{ fontSize: 13, color: theme.text.darkTurquoiseTwo, fontWeight: '600' }}>{t('Search-this-area')}</Label>
          </Pressable>
        )}
      </View>
    </View>
  );
};

export default MapFilter;
